import { forwardRef } from 'react';
import { cn } from '@immo/ui-kit';

type DateInputProps = {
  readonly id: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly type?: 'date' | 'datetime-local';
  readonly min?: string;
  readonly max?: string;
  readonly disabled?: boolean;
  readonly invalid?: boolean;
  readonly 'aria-describedby'?: string | undefined;
};

// Native date / datetime input with the browser's calendar-picker indicator
// tamed: `block`/`w-full` collapses the input's internal flex layout so the
// value sits on the left and `ml-auto` pushes the picker indicator to the
// right edge instead of jamming it next to the text. Mirrors the Input chrome.
// Forwards a ref so callers can focus the field (e.g. a "custom date" preset).
export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(function DateInput(props, ref) {
  const { id, value, onChange, type = 'date', min, max, disabled = false, invalid = false } = props;

  return (
    <input
      ref={ref}
      id={id}
      type={type}
      value={value}
      min={min}
      max={max}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      aria-invalid={invalid || undefined}
      aria-describedby={props['aria-describedby']}
      className={cn(
        'block h-control-md w-full rounded-md border bg-surface px-3 py-2 text-base font-sans text-text-primary',
        'focus-visible:outline-none focus-visible:border-interactive focus-visible:shadow-focus',
        'disabled:cursor-not-allowed disabled:opacity-60',
        '[&::-webkit-calendar-picker-indicator]:ml-auto [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-60 hover:[&::-webkit-calendar-picker-indicator]:opacity-100',
        invalid
          ? 'border-danger-500 focus-visible:border-danger-500 focus-visible:shadow-focus-danger'
          : 'border-border',
      )}
    />
  );
});
